/**
 * Zod input schemas for the 9 MCP tools (mcp-server-design §1.3). One schema per tool; the
 * registry declares JSON Schema for `tools/list` from these, and validates every `tools/call`
 * against them (INVALID_INPUT on failure). `outputSchema` is declared from the read handlers'
 * return types on the API side. Conventions: dates are `YYYY-MM-DD` (org-local), `limit` ≤50,
 * cursors opaque base64 keyset strings.
 */
import { z } from 'zod';
import { VISIT_STATES } from '../statemachines/visit';
import { PAYMENT_STATES, PAYMENT_METHODS } from '../statemachines/payment';
import { AGREEMENT_STATES } from '../statemachines/agreement';
import { ENTITY_TYPES } from '../constants/entities';

const ymd = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const cursor = z.string().min(1).optional();
// A limit ABOVE the hard max is CLAMPED (via clampLimit), never rejected (§3.4) — so we put
// NO upper bound here; any positive int is accepted and the server clamps it to MCP_LIMIT_MAX.
const limit = z.number().int().positive().optional();

export const whatsMyDayInput = z.object({
  date: ymd.optional(), // defaults to today in the org timezone, resolved server-side
});

export const getScheduleInput = z.object({
  date_from: ymd,
  date_to: ymd,
  tech_user_id: z.string().uuid().optional(),
  status: z.enum(VISIT_STATES).optional(),
  cursor,
  limit,
});

export const getJobInput = z
  .object({
    job_id: z.string().uuid().optional(),
    job_number: z.number().int().positive().optional(),
  })
  .refine((v) => Boolean(v.job_id) !== Boolean(v.job_number), {
    message: 'provide exactly one of job_id or job_number',
  });

export const customer360Input = z
  .object({
    query: z.string().min(1).optional(),
    customer_id: z.string().uuid().optional(),
  })
  .refine((v) => Boolean(v.query) !== Boolean(v.customer_id), {
    message: 'provide exactly one of query or customer_id',
  });

export const getArAgingInput = z.object({
  bucket: z.enum(['current', 'd30', 'd60', 'd90', 'd90_plus']).optional(),
  customer_id: z.string().uuid().optional(),
  cursor,
  limit,
});

export const listPaymentsInput = z.object({
  date_from: ymd,
  date_to: ymd,
  status: z.enum(PAYMENT_STATES).optional(),
  method: z.enum(PAYMENT_METHODS).optional(),
  cursor,
  limit,
});

export const getAgreementsInput = z.object({
  status: z.enum(AGREEMENT_STATES).optional(),
  unbooked_only: z.boolean().optional(),
  customer_id: z.string().uuid().optional(),
  cursor,
  limit,
});

export const getReconciliationStatusInput = z.object({
  period: z.union([z.literal('latest'), z.string().regex(/^\d{4}-\d{2}$/)]).optional(),
});

export const getHistoryInput = z.object({
  entity_type: z.enum(ENTITY_TYPES).optional(),
  entity_id: z.string().uuid().optional(),
  since: z.string().datetime().optional(),
  cursor,
  limit,
});

export type WhatsMyDayInput = z.infer<typeof whatsMyDayInput>;
export type GetScheduleInput = z.infer<typeof getScheduleInput>;
export type GetJobInput = z.infer<typeof getJobInput>;
export type Customer360Input = z.infer<typeof customer360Input>;
export type GetArAgingInput = z.infer<typeof getArAgingInput>;
export type ListPaymentsInput = z.infer<typeof listPaymentsInput>;
export type GetAgreementsInput = z.infer<typeof getAgreementsInput>;
export type GetReconciliationStatusInput = z.infer<typeof getReconciliationStatusInput>;
export type GetHistoryInput = z.infer<typeof getHistoryInput>;
